import { z } from 'zod';
import { colors } from '../../elements/colors';
import { spacing } from '../../elements/spacing';
import { sizing } from '../../elements/sizing';
import { typography } from '../../elements/typography';

export const STAR_RATING_MIN = 1;
export const STAR_RATING_MAX = 5;

export const starRatingPropsSchema = z.object({
  label: z.string().optional(),
  value: z.number().int().min(STAR_RATING_MIN).max(STAR_RATING_MAX).optional(),
  disabled: z.boolean().default(false),
  submitting: z.boolean().default(false),
  testID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
});

export type StarRatingProps = z.infer<typeof starRatingPropsSchema>;

/**
 * Web-only props. `decorative` draws the rating as a picture (no buttons, hidden
 * from assistive technology) and accepts half steps, so `value` widens to any
 * number from 0 to the maximum. Kept out of `starRatingPropsSchema`, so a
 * catalog document can never carry them and native never sees them.
 */
export const webStarRatingPropsSchema = starRatingPropsSchema.extend({
  value: z.number().min(0).max(STAR_RATING_MAX).optional(),
  decorative: z.boolean().optional(),
  iconSize: z.number().positive().optional(),
});

export type WebStarRatingSchemaProps = z.input<typeof webStarRatingPropsSchema>;

export const STAR_RATING_ICON_SIZE = sizing.icon.md;
export const STAR_RATING_TOUCH_SIZE = sizing.touchTarget.minimum;
export const STAR_RATING_GAP = spacing.s;
export const STAR_RATING_LABEL_GAP = spacing.sm;
export const STAR_RATING_LABEL_TYPOGRAPHY = typography.b1Bold;

export const STAR_RATING_SELECTED_COLOR = colors.icon.primary;
export const STAR_RATING_UNSELECTED_COLOR = colors.icon.tertiary;
export const STAR_RATING_DISABLED_OPACITY = 0.45;

export function starRatingAccessibleLabel(label?: string, accessibilityLabel?: string): string {
  return accessibilityLabel ?? label ?? 'Rating';
}

export function starRatingValues(): number[] {
  return Array.from({ length: STAR_RATING_MAX }, (_, index) => index + STAR_RATING_MIN);
}
